import { useForm } from "react-hook-form";

import AssignmentApi from "@/api/assignment-api";
import Button from "@/components/common/input/button";
import Input from "@/components/common/input/input";
import { partialObjectUpdater, handled, logRejection } from "@/helpers";
import { showSnackbar } from "@/hooks/snackbar";
import type { Assignment } from "@/types/assignment";

interface AssignmentSettingsFormValues {
  name: string;
}

// toggleFn arrives from the surrounding Modal through cloneElement, never from the call site.
interface AssignmentSettingsFormProps {
  assignment: Assignment;
  callbackFn?: () => void;
  toggleFn?: () => void;
}

export default function AssignmentSettingsForm({
  assignment,
  callbackFn,
  toggleFn,
}: AssignmentSettingsFormProps) {
  const { handleSubmit, register } = useForm<AssignmentSettingsFormValues>({
    defaultValues: { name: assignment.name },
  });

  const _handleSubmit = (formdata: AssignmentSettingsFormValues) => {
    AssignmentApi.update(
      partialObjectUpdater(assignment, {
        ...formdata,
      }),
    )
      .then(({ error }) => {
        if (error) {
          console.error(error);
          // The modal stays open so the name is still there to retry with.
          showSnackbar("Could not rename the assignment.", "warning");
          return;
        }
        if (callbackFn) callbackFn();
        toggleFn?.();
      })
      .catch(logRejection);
  };

  const _handleCancel = (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    toggleFn?.();
  };

  return (
    <form
      onSubmit={handled(handleSubmit(_handleSubmit))}
      className="mt-4 w-[32rem] rounded-lg bg-slate-800 p-8"
    >
      <Input label="Assignment name" type="text" id="name" register={register} />
      <div className="flex items-center gap-4">
        <Button type="submit">Submit</Button>
        <Button onClick={_handleCancel}>Cancel</Button>
      </div>
    </form>
  );
}
